(ns io.repliance.schemas.core
  (:require [clojure.string :as str]
            [malli.transform :as mt]
            [malli.util :as mu]))

(def -transformer
  (mt/transformer
    mt/strip-extra-keys-transformer
    mt/string-transformer
    (mt/transformer {:name :value->instant})))

(defn value->instant [value]
  (cond
    (number? value)
    #?(:clj (java.time.Instant/ofEpochMilli value)
       :cljs (js/Date. value))

    #?(:clj (instance? java.util.Date value))
    #?(:clj (.toInstant value))

    :else value))

(def Instant
  [:fn {:error/fn (fn [{:keys [value]} _]
                    (str value
                         " of type "
                         (type value)
                         " is not "
                         #?(:clj "a java.time.Instant"
                            :cljs "a js/Date")
                         " or is not formatted as a timestamp string"))
        :json-schema/type "string"
        :json-schema/format "date-time"
        :decode/string (fn [s]
                         (if (string? s)
                           #?(:clj (java.time.Instant/parse s)
                              :cljs (js/Date. s))
                           s))
        :decode/value->instant value->instant}
   (fn [value]
     (instance? #?(:clj java.time.Instant
                   :cljs js/Date)
                value))])

(def NonBlankString
  [:and
   :string
   [:fn {:error/fn (fn [_ _]
                     "Cannot be blank")
         :json-schema/type "string"
         :json-schema/format "string"
         :decode/string (fn [s]
                          s)
         :decode/value->string (fn [s]
                                 (when (and (string? s)
                                            (not (str/blank? s)))
                                   s))}
    (fn [value]
      (and (string? value)
           (not (str/blank? value))))]])

(def LegacyAnswer
  [:map {:closed false}
   [:id :uuid]
   [:question-id :uuid]
   ;; Metadata
   [:created Instant]
   [:author :string]
   ;; Data
   [:response :string]
   [:additional-information {:optional true} [:maybe :string]]
   [:meta
    [:map {:closed false}
     [:source {:optional true} [:enum "FAQ"]]
     [:reader_score number?]
     [:faq_content {:optional true} :string]
     [:document_content {:optional true} :string]
     [:transform_job_name {:optional true} :string]]]
   [:source-system {:optional true} [:maybe :string]]])

(def Option
  [:map
   [:id :uuid]
   [:content :string]
   [:selected :boolean]
   [:ordinal [:maybe :int]]
   [:multi :boolean]])

(def Analysis
  [:map
   [:id :uuid]
   [:content :string]
   [:created Instant]
   [:last-updated Instant]
   [:author :string]])

(def HumanAnswer
  [:map {:closed false}
   [:id :uuid]
   [:question-id :uuid]
   [:source :string]
   [:created Instant]
   [:response {:optional true} [:maybe :string]]
   [:additional-information {:optional true} [:maybe :string]]
   [:options {:optional true}
    [:sequential Option]]
   [:author :string]
   [:source-system {:optional true} [:maybe :string]]
   ;; TODO: based-answer-id needs to be migrated to answer uuid, also the data in json can be null now
   [:based-answer-id {:optional true} [:maybe [:or :string :uuid]]]
   [:analysis [:maybe Analysis]]])

(def InferredFaq
  [:map {:closed false}
   [:id :uuid]
   [:question-id :uuid]
   [:source :string]
   [:created Instant]
   [:score {:optional true
            :doc "Should be present, but was not in old data"}
    :double]
   [:transform_job_name {:optional true} :string]
   [:meta
    [:map {:closed false}
     [:faq
      [:map {:closed false}
       [:content {:doc "Some old data didn't have content, hmmm. New data does not have content field, since it is ambiquous. Better use \"question\" "
                  :optional true} [:maybe :string]]
       [:answer {:optional true} :string]
       [:additional-information {:optional true} [:maybe :string]]
       [:options {:optional true}
        [:maybe [:sequential Option]]]]]]]
   [:source-system {:optional true} [:maybe :string]]
   [:analysis [:maybe Analysis]]])

(def InferredDocument
  [:map {:closed false}
   [:id :uuid]
   [:question-id :uuid]
   [:source :string]
   [:created Instant]
   [:score {:optional true
            :doc "Should be present, but was not in old data"}
    :double]
   [:transform_job_name {:optional true} :string]
   [:answer
    {:optional true}
    :string]
   [:context {:optional true} :string]
   [:offsets_in_document {:optional true}
    [:sequential
     [:map {:closed false}
      [:start :int]
      [:end :int]]]]
   [:offsets_in_context {:optional true}
    [:sequential
     [:map {:closed false}
      [:start :int]
      [:end :int]]]]
   [:meta
    [:map {:closed false}
     [:classification {:doc "Yes/No/NA classification based on answer text. Legacy data not present any longer"
                       :optional true}
      [:map
       [:label {:optional true} [:maybe :string]]
       [:score {:optional true} :double]
       [:response_label {:optional true} [:maybe :string]]]]
     [:source_documents {:doc "List of documents from the Haystack Document store, based on the top-level :document_ids."
                         :optional true}
      [:sequential
       [:map {:closed false}
        [:content :string]]]]]]
   [:source-system {:optional true} [:maybe :string]]
   [:analysis [:maybe Analysis]]])

;; Note that there are quite many optional fields, to cope with various old forms of answers
(def Answer
  [:multi {:dispatch :source}
   [nil LegacyAnswer] ;; Current style of answer data
   ["human"
    HumanAnswer]
   ["inferred_faq"
    InferredFaq]
   ["inferred_document"
    InferredDocument]])

(def SelectedAnswer
  [:map
   [:answer :string]
   [:author :string]
   [:date Instant]
   [:item-id :string]
   [:questionnaire :uuid]
   [:source-system {:optional true} :string]])

(def QuestionNoteEvent
  [:multi {:dispatch :event-type}
   ["answer-changed"
    [:map
     [:event-type :string]
     [:answer :string]]]
   ["answer-edited"
    [:map
     [:event-type :string]
     [:answer :string]
     [:response :string]
     [:additional-information {:optional true} :string]]]
   ["status-changed"
    [:map
     [:event-type :string]
     [:status [:maybe :string]]]]
   ["attachment-added"
    [:map
     [:event-type :string]
     [:attachment :string]]]
   ["attachment-removed"
    [:map
     [:event-type :string]
     [:attachment :string]]]])

(def QuestionNote
  [:multi {:dispatch :type}
   ["message"
    [:map
     [:type :string]
     [:question-id :uuid]
     [:author :string]
     [:created Instant]
     [:content :string]]]
   ["event"
    [:map {:closed false}
     [:type :string]
     [:question-id :uuid]
     [:author :string]
     [:created Instant]
     [:data QuestionNoteEvent]]]])

;; TODO: This seems to describe two types of attachments, which have different shape.
;;       Would be good to make a multi-schema, so that all of the fields don't need to be optional.
(def NewQuestionnaireAttachment
  [:map
   [:type [:enum "document" "external-url"]]
   [:document-id {:optional true} :uuid]
   [:filename {:optional true} :string]
   [:url {:optional true} :string]
   [:linked-answers {:optional true} [:set :uuid]]])

(def QuestionnaireAttachment
  (mu/merge [:map
             [:questionnaire :uuid]
             [:item-id :string]
             [:created Instant]]
            NewQuestionnaireAttachment))

(def RowIndex :int)
(def ColumnIndex :int)

(def SheetConfigRefExceptions
  [:ref-exceptions
   {:optional true}
   [:map-of RowIndex
    [:map-of :keyword
     [:map
      [:ref [:maybe :string]]
      [:col :int]
      [:row :int]]]]])

(def SheetConfigValueExceptions
  [:value-exceptions
   {:optional true}
   [:map-of RowIndex
    [:map-of :keyword
     [:map [:value int?]]]]])

(def SheetConfigColumns
  [:columns
   {:optional true}
   [:map-of ColumnIndex keyword?]])

(def SheetConfigQuestionRows
  [:question-rows
   {:optional true}
   [:set RowIndex]])

(def SheetConfigDepthSource
  [:depth-source {:optional true}
   [:and
    keyword?
    [:enum :from-number :from-column :explicit]]])

(def SheetConfig
  [:map
   SheetConfigRefExceptions
   SheetConfigValueExceptions
   SheetConfigColumns
   SheetConfigQuestionRows
   SheetConfigDepthSource
   ;; TODO: This smells like derived data, so should go away.
   ;;       Probably we get to deal with this when working on more data extraction, like question categories.
   ;;       Then we can probably figure out how to ingest data iteratively, not only once.
   [:questions-extracted {:optional true
                          :documentation "Mark if questions have been extracted."}
    :boolean]])

(def Question
  [:map
   [:id :uuid]
   [:parent-id [:maybe :uuid]]
   [:questionnaire-id :uuid]
   [:content :string]
   [:display-name [:maybe :string]]
   [:status [:maybe :string]]
   [:section-name :string]
   [:section-id :uuid]
   [:section-ordinal :int]
   [:source-type :string]
   [:comment-required :boolean]
   [:ordinal :int]
   [:data [:maybe [:map {:closed false}]]]
   [:options [:sequential
              [:map
               [:id :uuid]
               [:element-context [:maybe :string]]
               [:content :string]
               [:multi :boolean]
               [:ordinal :int]]]]])

(def not-yet-evaluated-answer-scope
  "Not yet evaluated")

(def completed-by-analyst-answer-scope
  "Completed by Analyst")

(def AnswerScope
  [:map
   [:content :string]
   [:action [:maybe :string]]
   [:created Instant]
   [:author :string]])
